import signInIllustration from "@/assets/froppio-sign-in.svg";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { Button } from "@/shared/components/shadcn/Button";
import { Input } from "@/shared/components/shadcn/Input";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { FormPage } from "@/shared/components/ui/FormPage";
import { FormField } from "@/shared/components/ui/FormField";
import { SectionAction } from "@/shared/components/ui/SectionAction";
import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { EntryHeader } from "../components/EntryHeader";

export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submitting = useRef(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from: unknown = location.state?.from;
  const returnPath = typeof from === "string" && from.startsWith("/") && !from.startsWith("//") && !from.includes("\\")
    ? from
    : "/";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting.current) return;
    submitting.current = true;
    setError("");
    setLoading(true);
    try {
      await signIn(email, password);
      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      submitting.current = false;
      setLoading(false);
    }
  };

  return (
    <FormPage
      header={null}
      onSubmit={handleSubmit}
      narrow
      contentClassName="flex min-h-full flex-col justify-center space-y-0 py-6"
      actions={
        <Button type="submit" size="touch" disabled={loading}>
          {loading ? "Signing in..." : "Sign in"}
        </Button>
      }
    >
      <EntryHeader
        title="Sign in"
        illustration={signInIllustration}
        illustrationAlt="Froppio holding a key and waving welcome back"
        hideTitle
      />
      <div className="space-y-6" aria-busy={loading}>
        {error && <ErrorState message={error} />}
        <FormField id="email" label="Email" className="[&_label>span]:hidden">
          <Input name="email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required readOnly={loading} />
        </FormField>
        <FormField id="password" label="Password" className="[&_label>span]:hidden">
          <Input name="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required readOnly={loading} />
        </FormField>
      </div>
      <div className="mt-2 flex justify-end">
        <SectionAction asChild>
          <Link to="/forgot-password" state={location.state}>Forgot the password?</Link>
        </SectionAction>
      </div>
      <p className="text-center text-sm text-muted-foreground">
        New to Froppio?{" "}
        <SectionAction asChild>
          <Link to="/welcome" state={location.state}>Get started</Link>
        </SectionAction>
      </p>
    </FormPage>
  );
}
